import { ShoppingBagIcon, PackageIcon, TrendingUpIcon } from '../Icons'
import { Product } from '../../data/products'

interface LabOrder {
  id: string
  items: Product[]
  total: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'paid'
  createdAt: string
  customerName: string
}

interface LabAdminPanelV1Props {
  onLog: (message: string) => void
  orders: LabOrder[]
  onConfirmOrder: (orderId: string) => void
  onCancelOrder: (orderId: string) => void
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  accent: '#9333ea',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  red: '#ef4444',
  blue: '#3b82f6',
}

export default function LabAdminPanelV1({ orders, onConfirmOrder, onCancelOrder }: LabAdminPanelV1Props) {
  const stats = [
    { label: 'Всего', value: orders.length.toString(), icon: ShoppingBagIcon, color: colors.accent },
    { label: 'Подтверждено', value: orders.filter(o => o.status === 'confirmed').length.toString(), icon: TrendingUpIcon, color: colors.green },
    { label: 'Ожидают', value: orders.filter(o => o.status === 'pending').length.toString(), icon: PackageIcon, color: colors.yellow },
  ]

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      color: colors.text,
      padding: '16px',
      paddingBottom: '140px',
    }}>
      {/* Заголовок */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          margin: 0,
          fontSize: '24px',
          fontWeight: 700,
          background: `linear-gradient(135deg, ${colors.accent}, #a855f7)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Админ v1
        </h1>
        <p style={{
          margin: '4px 0 0 0',
          fontSize: '13px',
          color: colors.textMuted,
        }}>
          Минималистичный стиль
        </p>
      </div>

      {/* Статистика */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {stats.map((stat, idx) => (
          <div
            key={idx}
            style={{
              background: colors.cardBg,
              borderRadius: '12px',
              padding: '16px 12px',
              textAlign: 'center',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <stat.icon size={24} color={stat.color} />
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              marginTop: '8px',
              color: stat.color,
            }}>
              {stat.value}
            </div>
            <div style={{
              fontSize: '11px',
              color: colors.textMuted,
              marginTop: '4px',
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Заказы */}
      <div style={{
        background: colors.cardBg,
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          fontWeight: 600,
        }}>
          Активные заказы
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {orders.map(order => (
            <div
              key={order.id}
              style={{
                background: '#1c1c22',
                borderRadius: '10px',
                padding: '14px',
                opacity: order.status === 'cancelled' ? 0.5 : 1,
                border: `1px solid ${
                  order.status === 'confirmed' ? colors.green + '40' :
                  order.status === 'paid' ? colors.blue + '40' :
                  order.status === 'cancelled' ? colors.red + '40' :
                  'rgba(255, 255, 255, 0.05)'
                }`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                    {order.id}
                  </div>
                  <div style={{ fontSize: '13px', color: colors.textMuted }}>
                    {order.customerName} • {order.items.length} товаров
                  </div>
                </div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.accent }}>
                  ₽{order.total.toLocaleString()}
                </div>
              </div>

              {order.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => onConfirmOrder(order.id)}
                    style={{
                      flex: 1,
                      background: colors.green,
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✓ Подтвердить
                  </button>
                  <button
                    onClick={() => onCancelOrder(order.id)}
                    style={{
                      flex: 1,
                      background: colors.red,
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ✕ Отменить
                  </button>
                </div>
              )}

              {order.status === 'confirmed' && (
                <div style={{
                  background: colors.green + '20',
                  border: `1px solid ${colors.green}`,
                  borderRadius: '8px',
                  padding: '8px',
                  textAlign: 'center',
                  color: colors.green,
                  fontSize: '13px',
                  fontWeight: 600,
                }}>
                  ✓ Подтверждён - ожидает оплаты
                </div>
              )}

              {order.status === 'paid' && (
                <div style={{
                  background: colors.blue + '20',
                  border: `1px solid ${colors.blue}`,
                  borderRadius: '8px',
                  padding: '8px',
                  textAlign: 'center',
                  color: colors.blue,
                  fontSize: '13px',
                  fontWeight: 600,
                }}>
                  💳 Оплачен
                </div>
              )}

              {order.status === 'cancelled' && (
                <div style={{
                  background: colors.red + '20',
                  border: `1px solid ${colors.red}`,
                  borderRadius: '8px',
                  padding: '8px',
                  textAlign: 'center',
                  color: colors.red,
                  fontSize: '13px',
                  fontWeight: 600,
                }}>
                  ✕ Отменён
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
